import i18n from '../../../i18n';
import { formatAddress, arraySortAscendingFunction } from '../../utilities';

export const createKvkQueryObject = (addressdata) => {
    // Mapping from fields in addressdata to fields in endpoint Kvk
    let query = {};
    query['InclusiefInactieveRegistraties'] = false;
    query['huisnummer'] = addressdata['huisNummer'] || addressdata['number'];
    query['postcode'] = addressdata['postCode'];
    query['postbusnummer'] = addressdata['postbusnummer'];

    if (addressdata['huisLetter']) {
        query['huisletter'] = addressdata['huisLetter'];
    }
    if (addressdata['huisNummerToevoeging']) {
        if (query['huisletter']) {
            query['huisletter'].concat(addressdata['huisNummerToevoeging']);
        } else {
            query['huisletter'] = (addressdata['huisNummerToevoeging']);
        }
    }
    return query;
};

export const getHandelsRegisterData = (searchData, companiesJson) => {
    let items = [];
    let fields = [];

    if (!companiesJson) {
        return {
            items,
            fields
        };
    }

    for (let i = 0, l = companiesJson.length; i < l; i++) {
        items.push({
            id: i,
            nedGlobeRowId: i, // provide a unique ID column to avoid conflicts with the ID column in the data
            kvkNummer: companiesJson[i].kvkNummer,
            rsin: companiesJson[i].rsin,
            vestigingsnummer: companiesJson[i].vestigingsnummer,
            handelsnaam: companiesJson[i].naam,
            type: companiesJson[i].type,
            straatnaam: companiesJson[i].adres?.binnenlandsAdres?.straatnaam,
            huisnummer: companiesJson[i].adres?.binnenlandsAdres?.huisnummer,
            huisnummerToevoeging: companiesJson[i].adres?.binnenlandsAdres?.huisletter,
            postcode: companiesJson[i].adres?.binnenlandsAdres?.postcode,
            plaats: companiesJson[i].adres?.binnenlandsAdres?.plaats,
            actief: companiesJson[i].actief,
            vervallenNaam: companiesJson[i].vervallenNaam,
            showRelation: companiesJson[i].type !== 'rechtspersoon'
        });
    }

    if (items.length > 0) {
        fields.push(
            {
                key: 'kvkNummer',
                label: i18n.t('kvk.kvkNumber'),
                sortable: true,
                showField: true
            },
            {
                key: 'rsin',
                label: i18n.t('kvk.rsinNumber'),
                sortable: true,
                showField: !!searchData.rsin && items.some(el => el.rsin)
            },
            {
                key: 'vestigingsnummer',
                label: i18n.t('kvk.branchNumber'),
                sortable: true,
                showField: true
            },
            {
                key: 'handelsnaam',
                label: i18n.t('kvk.tradeName'),
                sortable: true,
                showField: true
            },
            {
                key: 'type',
                label: i18n.t('kvk.type'),
                sortable: true,
                showField: true
            },
            {
                key: 'straatnaam',
                label: i18n.t('kvk.straatnaam'),
                sortable: true,
                showField: true
            },
            {
                key: 'huisnummer',
                label: i18n.t('kvk.huisnummer'),
                sortable: true,
                showField: items.some(el => el.huisnummer)
            },
            {
                key: 'huisnummerToevoeging',
                label: i18n.t('kvk.huisnummerToevoeging'),
                sortable: true,
                showField: items.some(el => el.huisnummerToevoeging)
            },
            {
                key: 'postbusnummer',
                label: i18n.t('kvk.postbusnummer'),
                sortable: true,
                showField: items.some(el => el.postbusnummer)
            },
            {
                key: 'postcode',
                label: i18n.t('kvk.postcode'),
                sortable: true,
                showField: items.some(el => el.postcode)
            },
            {
                key: 'plaats',
                label: i18n.t('kvk.woonplaats'),
                sortable: true,
                showField: true
            },
            {
                key: 'actief',
                label: i18n.t('kvk.active'),
                sortable: true,
                showField: true
            },
            {
                key: 'vervallenNaam',
                label: i18n.t('kvk.expiredName'),
                sortable: true,
                showField: true
            }
        );
    }

    // sort items
    items.sort(arraySortAscendingFunction('kvkNummer'));

    return {
        items,
        fields: fields.filter(({ showField }) => showField),
        sortBy: 'kvkNummer',
        selectable: true,
        relation: {
            name: i18n.t('kvk.locationProfile'),
            title: `${String(i18n.t('relations.showRelated'))} ${(String(i18n.t('kvk.locationProfile').toLowerCase()))}`,
            type: 'link',
            action: 'GetKvkVestigingsProfiel'
        }
    };
};

export const getVestigingsProfielData = (vestigingsJson) => {
    const formatDate = (datum) => {
        if (datum) {
            return `${datum.substring(6, 8)}-${datum.substring(
                4,
                6
            )}-${datum.substring(0, 4)}`;
        } else {
            return '';
        }
    };

    let items = [];
    let fields = [];

    if (!vestigingsJson) {
        return {
            items,
            fields
        };
    }

    let bezoekadres = vestigingsJson.adressen.filter(({ type }) => type === 'bezoekadres');
    bezoekadres = bezoekadres ? bezoekadres[0] : null;
    let correspondentieadres = vestigingsJson.adressen.filter(({ type }) => type === 'correspondentieadres');
    correspondentieadres = correspondentieadres ? correspondentieadres[0] : null;

    items.push({
        id: 0,
        vestigingsnummer: vestigingsJson.vestigingsnummer,
        kvkNummer: vestigingsJson.kvkNummer,
        rsin: vestigingsJson.rsin,
        eersteHandelsnaam: vestigingsJson.eersteHandelsnaam,
        totaalWerkzamePersonen: vestigingsJson.totaalWerkzamePersonen,
        formeleRegistratiedatum: formatDate(vestigingsJson.formeleRegistratiedatum),
        geoData: bezoekadres?.geoData,
        commercieleVestiging: vestigingsJson.indCommercieleVestiging,
        hoofdvestiging: vestigingsJson.indHoofdvestiging,
        sbiActiviteiten: vestigingsJson.sbiActiviteiten ? vestigingsJson.sbiActiviteiten.map(({ sbiOmschrijving }) => sbiOmschrijving).join(', ') : '',
        bezoekAdres: bezoekadres ? formatAddress(bezoekadres) : '',
        correspondentieAdres: correspondentieadres ? formatAddress(correspondentieadres) : ''
    });

    if (items.length > 0) {
        fields.push(
            {
                key: 'vestigingsnummer',
                label: i18n.t('kvk.branchNumber'),
                sortable: true
            },
            {
                key: 'kvkNummer',
                label: i18n.t('kvk.kvkNumber'),
                sortable: true
            },
            {
                key: 'rsin',
                label: i18n.t('kvk.rsinNumber'),
                sortable: true
            },
            {
                key: 'eersteHandelsnaam',
                label: i18n.t('kvk.tradeName'),
                sortable: true
            },
            {
                key: 'totaalWerkzamePersonen',
                label: i18n.t('kvk.totalEmployedPersons'),
                sortable: true
            },
            {
                key: 'formeleRegistratiedatum',
                label: i18n.t('kvk.formalRegistrationDate'),
                sortable: true
            },
            {
                key: 'commercieleVestiging',
                label: i18n.t('kvk.commercialEstablishment'),
                sortable: true
            },
            {
                key: 'hoofdvestiging',
                label: i18n.t('kvk.headquarter'),
                sortable: true
            },
            {
                key: 'bezoekAdres',
                label: i18n.t('kvk.bezoekadres'),
                sortable: true
            },
            {
                key: 'correspondentieAdres',
                label: i18n.t('kvk.correspondentieadres'),
                sortable: true
            },
            {
                key: 'sbiActiviteiten',
                label: i18n.t('kvk.activities'),
                sortable: true
            }
        );
    }

    // sort items
    items.sort(arraySortAscendingFunction('vestigingsnummer'));

    return {
        items,
        fields,
        sortBy: 'vestigingsnummer',
        selectable: true,
        rowClick: { action: 'highlightVestiging' }
    };
};
